/**
 * @file The cross-source contribution contract — the typed shape every
 *       source (panel) emits into the Dashboard, plus the source-agnostic
 *       aggregation/sort rule that merges them.
 * @module @labcraft/admin/ui/sources/contract
 *
 * Pure types + a stateless helper — no CSS, no React, no DOM. Lives in
 * a neutral `ui/sources/` directory so the Dashboard (#890) and every
 * panel can import it without a directional dependency between them.
 * The Dashboard reads these; each panel builds them from its own
 * already-typed hook data. This keeps the Dashboard from accreting a
 * per-panel translator (umbrella #883, part #889).
 */

/**
 * The sources the operator console synthesizes across. Each panel maps
 * to one tag; `BILLING` is the standing "not connected yet" source
 * until a billing service exists. `SOCIAL` is reserved for the Social
 * panel (#350/#331).
 */
export type SourceTag =
  | 'SOCIAL'
  | 'SECRETS'
  | 'ACCOUNT'
  | 'EMAIL'
  | 'HYPERVISOR'
  | 'COURSES'
  | 'BILLING'

/**
 * Severity ordering key for a Needs-you item. Errors/incidents sort
 * first, then warnings, then informational nudges. The string values
 * are the sort buckets; {@link SourceContribution.SEVERITY_RANK} maps
 * them to a numeric order.
 */
export type NeedsYouSeverity = 'incident' | 'warning' | 'info'

/**
 * Per-route publish-trust posture. Defined in the contract because it
 * is part of the Needs-you item *shape*; the posture part (#897) owns
 * the `--pp-*` tokens and the pill component that render it. An item
 * with no posture renders a plain chevron affordance instead.
 */
export type PostureKind = 'auto' | 'approve' | 'remind' | 'mixed' | 'error'

/**
 * A source-agnostic Needs-you item — one row in the Dashboard's unified
 * "what needs me?" inbox.
 *
 * @property id - Stable unique id (source-prefixed) for React keys + dedupe.
 * @property source - Which source emitted the item.
 * @property severity - Sort bucket; incidents first.
 * @property title - One-line headline ("Mastodon token expired").
 * @property sub - Secondary context ("brain · last rotated 94 days ago").
 * @property route - The panel path a click navigates to ("/secrets").
 * @property posture - Optional posture descriptor; absent → chevron affordance.
 */
export type NeedsYouItem = {
  id: string
  source: SourceTag
  severity: NeedsYouSeverity
  title: string
  sub: string
  route: string
  posture?: PostureKind
}

/**
 * Health of a source's tile dot — `live` (a connected, reporting
 * service) vs `muted` (not connected yet, or unreachable).
 */
export type SourceHealth = 'live' | 'muted'

/**
 * The three honest states a Sources tile renders. `needs-you` is amber
 * with a count, `ok` is green, `not-connected` is a muted em dash.
 */
export type SourceTileState = 'needs-you' | 'ok' | 'not-connected'

/**
 * A Sources-board tile descriptor — one per service.
 *
 * @property source - Which source this tile represents.
 * @property label - Operator-facing service name ("Hypervisor").
 * @property health - Live dot vs muted dot.
 * @property state - needs-you (amber) / ok (green) / not-connected (—).
 * @property needsYouCount - Count shown when `state === 'needs-you'`.
 * @property stat - A notable one-line stat ("3 running · 12 GiB"), or `—`.
 * @property route - The panel path a click on a live tile navigates to.
 */
export type SourcesTile = {
  source: SourceTag
  label: string
  health: SourceHealth
  state: SourceTileState
  needsYouCount: number
  stat: string
  route: string
}

/**
 * What a single source contributes to the Dashboard: zero or more
 * Needs-you items + exactly one Sources tile. Panels build this from
 * their hook data and the Dashboard merges every source's contribution.
 *
 * @property items - The source's Needs-you items (may be empty).
 * @property tile - The source's Sources-board tile.
 */
export type SourceContributionData = {
  items: NeedsYouItem[]
  tile: SourcesTile
}

/**
 * The source-agnostic merge + ordering rule for Needs-you items. Static
 * because it carries no state; it is the one place the "how N sources'
 * items combine and order" contract is defined, so the Dashboard never
 * hand-rolls per-panel ordering.
 */
export class SourceContribution {
  /** Numeric rank per severity bucket; lower sorts first. */
  static readonly SEVERITY_RANK: Record<NeedsYouSeverity, number> = {
    incident: 0,
    warning: 1,
    info: 2,
  }

  /**
   * Merge each source's Needs-you items into one inbox, ordered
   * errors/incidents first then by source for a stable grouping.
   *
   * @param contributions - One item array per contributing source.
   * @returns A single severity-sorted, stable inbox.
   */
  static aggregate(contributions: NeedsYouItem[][]): NeedsYouItem[] {
    return SourceContribution.sort(contributions.flat())
  }

  /**
   * Sort a flat Needs-you list: incidents first, then warnings, then
   * info; ties broken by source then id so the order is deterministic.
   *
   * @param items - The items to order (not mutated).
   * @returns A new, ordered array.
   */
  static sort(items: NeedsYouItem[]): NeedsYouItem[] {
    return [...items].sort((a, b) => {
      const bySeverity =
        SourceContribution.SEVERITY_RANK[a.severity] -
        SourceContribution.SEVERITY_RANK[b.severity]
      if (bySeverity !== 0) return bySeverity
      if (a.source !== b.source) return a.source < b.source ? -1 : 1
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
    })
  }

  /**
   * Tally how many sources are live vs the total — drives the
   * Dashboard head's "N of M sources live" line.
   *
   * @param tiles - Every source's tile.
   * @returns `{ live, total }` counts.
   */
  static liveCount(tiles: SourcesTile[]): { live: number; total: number } {
    return {
      live: tiles.filter(t => t.health === 'live').length,
      total: tiles.length,
    }
  }
}
